import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../ui/button.jsx';
import { Input } from '../ui/input.jsx';
import { Label } from '../ui/label.jsx';
import { Skeleton } from '../ui/skeleton.jsx';
import { SegmentedControl } from '../ui/segmented.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.jsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.jsx';
import HistoryGroup from '../history/HistoryGroup';
import {
  HISTORY_RETENTION_DAYS,
  TABLE_GROUPS,
  TABLE_LABELS,
  groupHistory,
  recordLabel,
  recordLink,
} from '../../lib/history';
import { PURGE_AFTER_DAYS, canUpdate, purgeDate } from '../../lib/records';
import { recycleBin, teamHistory } from '../../services/history';
import { restoreRecord } from '../../services/records';
import { getTeamMembers } from '../../services/appwrite';
import { useAuth } from '../../context/AuthContext';

const VIEWS = [
  { value: 'changes', label: 'All changes' },
  { value: 'bin', label: 'Recycle bin' },
];

const ALL = 'all';

const formatDay = (value) => new Date(value).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });

/** The start or end of a local calendar day, as the ISO time the query compares. */
const dayBoundary = (day, end) => (day ? new Date(`${day}T${end ? '23:59:59.999' : '00:00:00'}`).toISOString() : null);

/**
 * Settings → History, for admins. Two views:
 *
 *   All changes   every recorded change to the team's records, newest first,
 *                 filtered by record type, person and date.
 *   Recycle bin   everything deleted in the last PURGE_AFTER_DAYS, read from the
 *                 records themselves (not from history), with Restore.
 */
export default function HistorySettings() {
  const [view, setView] = useState('changes');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">History</h2>
          <p className="text-muted-foreground mt-1">
            {view === 'changes'
              ? `Who changed what across your records. Changes appear a few minutes after they're made and are kept for ${HISTORY_RETENTION_DAYS} days.`
              : `Deleted items stay here for ${PURGE_AFTER_DAYS} days, then are removed for good.`}
          </p>
        </div>
        <SegmentedControl options={VIEWS} value={view} onChange={setView} />
      </div>
      {view === 'changes' ? <AllChanges /> : <RecycleBin />}
    </div>
  );
}

function AllChanges() {
  const [group, setGroup] = useState(ALL);
  const [actor, setActor] = useState(ALL);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [members, setMembers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [cursor, setCursor] = useState({ before: null, done: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getTeamMembers().then(setMembers).catch(() => setMembers([]));
  }, []);

  const filters = useMemo(() => ({
    tables: group === ALL ? null : TABLE_GROUPS.find((g) => g.id === group)?.tables,
    actor: actor === ALL ? null : actor,
    from: dayBoundary(from, false),
    to: dayBoundary(to, true),
  }), [group, actor, from, to]);

  const load = useCallback(async (before) => {
    setLoading(true);
    setError('');
    try {
      const page = await teamHistory({ ...filters, before });
      setEntries((prev) => (before ? [...prev, ...page.entries] : page.entries));
      setCursor({ before: page.before, done: page.done });
    } catch (err) {
      console.error('Could not load history:', err);
      setError('History could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(null); }, [load]);

  const groups = useMemo(() => groupHistory(entries), [entries]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1">
          <Label htmlFor="history-type">Record type</Label>
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger id="history-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All records</SelectItem>
              {TABLE_GROUPS.map((g) => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="history-person">Person</Label>
          <Select value={actor} onValueChange={setActor}>
            <SelectTrigger id="history-person"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Everyone</SelectItem>
              {members.map((m) => (
                <SelectItem key={m.userId} value={m.userId}>{m.userName || m.userEmail}</SelectItem>
              ))}
              <SelectItem value="system">System (automatic changes)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="history-from">From</Label>
          <Input id="history-from" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="history-to">To</Label>
          <Input id="history-to" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {!error && loading && entries.length === 0 && (
        <div className="space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      )}
      {!error && !loading && groups.length === 0 && (
        <p className="text-sm text-muted-foreground">No changes match these filters.</p>
      )}
      {groups.length > 0 && (
        <ul className="divide-y">
          {groups.map((g) => <HistoryGroup key={g.key} group={g} linkRecord />)}
        </ul>
      )}
      {!cursor.done && entries.length > 0 && (
        <Button variant="outline" size="sm" onClick={() => load(cursor.before)} disabled={loading}>
          {loading ? 'Loading…' : 'Load older'}
        </Button>
      )}
    </div>
  );
}

function RecycleBin() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    let cancelled = false;
    recycleBin()
      .then((result) => { if (!cancelled) setItems(result); })
      .catch((err) => {
        console.error('Could not load the recycle bin:', err);
        if (!cancelled) setError('The recycle bin could not be loaded.');
      });
    return () => { cancelled = true; };
  }, []);

  const handleRestore = async (item) => {
    setRestoring(item.row.$id);
    try {
      await restoreRecord(item.table, item.row);
      setItems((prev) => prev.filter((i) => i.row.$id !== item.row.$id));
      toast.success(`${TABLE_LABELS[item.table] || 'Item'} restored`);
    } catch (err) {
      toast.error(err?.message || 'Could not restore this');
    } finally {
      setRestoring(null);
    }
  };

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!items) return <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>;
  if (items.length === 0) return <p className="text-sm text-muted-foreground">The recycle bin is empty.</p>;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Item</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Deleted</TableHead>
          <TableHead>Removed for good</TableHead>
          <TableHead className="w-0" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => {
          const { table, row } = item;
          const parentRowId = row.quoteRequestId
            || (row.relatedRecordType === 'quote_requests' ? row.relatedRecordId : null);
          const link = recordLink(table, row.$id, parentRowId ? 'quote_requests' : null, parentRowId);
          const name = recordLabel(table, row);
          return (
            <TableRow key={`${table}:${row.$id}`}>
              <TableCell>
                {link ? <Link to={link} className="font-medium hover:underline">{name}</Link> : <span className="font-medium">{name}</span>}
                {item.detail && <div className="text-xs text-muted-foreground">{item.detail}</div>}
              </TableCell>
              <TableCell className="text-muted-foreground">{TABLE_LABELS[table]}</TableCell>
              <TableCell>
                {formatDay(row.deletedAt)}
                {item.deletedBy && <div className="text-xs text-muted-foreground">by {item.deletedBy}</div>}
              </TableCell>
              <TableCell>{formatDay(purgeDate(row))}</TableCell>
              <TableCell>
                {canUpdate(row, user) && (
                  <Button type="button" variant="outline" size="sm" onClick={() => handleRestore(item)} disabled={restoring === row.$id}>
                    <RotateCcw />
                    {restoring === row.$id ? 'Restoring…' : 'Restore'}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
